"use client";

import { BellIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type Notification = {
  id: string;
  title: string;
  message: string;
  ticketId?: string;
  type:
    | "ticket_assigned"
    | "ticket_finished_by_creator"
    | "scope_pending_tbd"
    | "admin_pending_validation";
  read: boolean;
  createdAt: string;
};

function getTargetPath(notification: Notification): string {
  if (notification.ticketId) {
    return `/ticket/${notification.ticketId}`;
  }

  if (notification.type === "admin_pending_validation") {
    return "/admin";
  }

  return "/";
}

export default function NotificationIcon() {
  const [items, setItems] = useState<Notification[]>([]);
  const router = useRouter();

  const load = async () => {
    const resp = await fetch("/api/notifications", { cache: "no-store" });
    if (!resp.ok) return;
    setItems(await resp.json());
  };

  useEffect(() => {
    load();
  }, []);

  const unread = useMemo(() => items.filter((i) => !i.read).length, [items]);

  const markRead = async (id: string) => {
    await fetch(`/api/notifications/${id}/read`, { method: "PATCH" });
    await load();
  };

  const markAllRead = async () => {
    await fetch(`/api/notifications/read-all`, { method: "PATCH" });
    await load();
  };

  const openNotification = async (notification: Notification) => {
    await markRead(notification.id);
    router.push(getTargetPath(notification));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative">
          <BellIcon className="h-4 w-4" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 text-[10px] rounded-full bg-red-500 text-white px-1">
              {unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80">
        <DropdownMenuLabel className="flex justify-between items-center">
          Notifications
          <button className="text-xs underline" onClick={markAllRead}>
            Mark all read
          </button>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 && <DropdownMenuItem>No notifications</DropdownMenuItem>}
        {items.map((n) => (
          <DropdownMenuItem key={n.id} onClick={() => openNotification(n)}>
            <div className="flex items-start gap-2 w-full cursor-pointer">
              <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${!n.read ? "bg-blue-500" : "bg-transparent"}`} />
              <div className="flex flex-col">
                <span className={!n.read ? "font-semibold" : ""}>{n.title}</span>
                <span className="text-xs text-muted-foreground">{n.message}</span>
              </div>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
